import axios from "axios";
import emailjs from "@emailjs/browser";
import { EMAIL_CONFIG } from "../emailConfig";



  const API_BASE_URL = 'https://funnelslanding.co.kr';
// ==================== URL 검증 관련 ====================
/**
 * URL 코드를 검증하고 설정 정보를 가져오는 API
 * @param {string} urlCode - 검증할 URL 코드
 * @returns {Promise<{id: number, ad_number: string, hospital_code: string}>}
 */
export const validateUrlCode = async (urlCode) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/randing/${urlCode}`);
    return response.data;
  } catch (error) {
    console.error("URL 코드 검증 실패:", error);
    throw error;
  }
};

// ==================== 클릭 추적 관련 ====================
/**
 * 페이지 방문 클릭 카운트를 기록하는 API
 * @param {number} urlCodeSettingId - URL 코드 설정 ID
 */
export const trackPageClick = async (urlCodeSettingId) => {
  try {
    await axios.post(`${API_BASE_URL}/api/randing/click/${urlCodeSettingId}`);
  } catch (error) {
    console.error("클릭 추적 실패:", error);
    // 추적 실패는 무시 (사용자 경험에 영향 없음)
  }
};

// ==================== 고객 폼 제출 관련 ====================
/**
 * 일반 고객 폼 데이터를 제출하는 API
 * @param {Object} formData - 폼 데이터
 * @param {string} formData.name - 고객 이름
 * @param {string} formData.phoneNumber - 전화번호
 * @param {string} formData.selectedOption - 선택 옵션
 * @param {string} formData.date - 제출 날짜
 * @param {string} formData.urlCode - URL 코드
 * @param {number} formData.urlCodeSettingId - URL 코드 설정 ID
 * @param {string} [formData.kakaotalk] - 카카오톡 ID
 * @param {string} [formData.selectedValue] - 선택 값
 */
export const submitCustomerForm = async (formData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/randing/customer`, formData);
    return response;
  } catch (error) {
    console.error("고객 폼 제출 실패:", error);
    throw error;
  }
};

/**
 * 크래프트 고객 폼 데이터를 제출하는 API
 * @param {Object} formData - 크래프트 폼 데이터 (apiUrl 포함)
 */
export const submitCraftCustomerForm = async (formData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/randing/customer/craft`, formData);
    return response;
  } catch (error) {
    console.error("크래프트 고객 폼 제출 실패:", error);
    throw error;
  }
};

/**
 * 백업용 데이터 로그 API
 * @param {Object} backupData - 백업 데이터
 * @param {string} backupData.name - 고객 이름
 * @param {string} backupData.phoneNumber - 전화번호
 * @param {string} backupData.date - 제출 날짜
 */
export const submitBackupLog = async (backupData) => {
  try {
    await axios.post(`${API_BASE_URL}/api/randing/datalog`, backupData);
    console.log("백업 API 성공적으로 완료");
  } catch (error) {
    console.error("백업 API 호출 실패:", error);
    throw error;
  }
};

/**
 * 에러 발생 시 이메일 알림 전송 (프론트엔드에서 직접 전송)
 * EmailJS를 사용하여 백엔드 없이 이메일 전송
 * @param {Object} errorData - 에러 정보
 * @param {string} errorData.name - 고객 이름
 * @param {string} errorData.phoneNumber - 전화번호
 * @param {string} errorData.date - 에러 발생 시간
 * @param {string} errorData.errorMessage - 에러 메시지
 * @param {string} errorData.urlCode - URL 코드
 */
export const sendErrorNotification = async (errorData) => {
  try {
    // EmailJS 초기화
    emailjs.init(EMAIL_CONFIG.PUBLIC_KEY);
    
    // 이메일 템플릿 파라미터
    const templateParams = {
      to_email: "funnelspd@gmail.com",
      name: errorData.name || "알 수 없음",
      phoneNumber: errorData.phoneNumber || "알 수 없음",
      date: errorData.date || new Date().toLocaleString("ko-KR"),
      urlCode: errorData.urlCode || "알 수 없음",
      errorType: "백업 API 실패",
      errorMessage: errorData.errorMessage || "상세 에러 메시지 없음",
      userAgent: navigator.userAgent,
      currentUrl: window.location.href
    };
    
    // 이메일 전송
    const response = await emailjs.send(
      EMAIL_CONFIG.SERVICE_ID,
      EMAIL_CONFIG.TEMPLATE_ID,
      templateParams
    );
    
    console.log("에러 알림 이메일 전송 완료:", response);
  } catch (error) {
    console.error("이메일 알림 전송 실패:", error);
    // 이메일 전송 실패는 무시 (사용자 경험에 영향 없음)
  }
};

// ==================== 외부 픽셀/추적 관련 ====================
/**
 * Google Analytics 이벤트 추적
 * @param {Object} eventData - 이벤트 데이터
 */
export const trackGoogleAnalytics = (eventData) => {
  if (window.gtag && typeof window.gtag === "function") {
    // 폼 제출 이벤트
    window.gtag("event", "form_submit", {
      event_category: "Form",
      event_label: "Contact Form Submission",
      value: 1,
    });

    // 사용자 데이터 설정 (전화번호) - 기존 방식 유지 (DOM에서 직접 가져옴)
    window.gtag("set", "user_data", {
      phone_number: Array.from(document.querySelectorAll('[type="tel"]'))
        .map(function (e) {
          return e.value;
        })
        .join("")
        .replace(/^0/, "+82"),
    });

    // 전환 이벤트
    window.gtag("event", "conversion", {
      send_to: "AW-17369469748/yZGYCKuT8vIaELSmtNpA",
    });
  }
};

/**
 * Facebook Pixel 이벤트 추적
 */
export const trackFacebookPixel = () => {
  if (window.fbq && typeof window.fbq === "function") {
    window.fbq("track", "CompleteRegistration");
  }
};

/**
 * Karrot Pixel 이벤트 추적
 * @param {string} eventName - 이벤트 이름 (SubmitApplication, ViewPage)
 */
export const trackKarrotPixel = (eventName) => {
  if (window.karrotPixel) {
    window.karrotPixel.track(eventName);
  }
};

/**
 * Karrot Pixel 초기화
 */
export const initKarrotPixel = () => {
  const karrotPixelScript = document.createElement("script");
  karrotPixelScript.src =
    "https://karrot-pixel.business.daangn.com/0.4/karrot-pixel.umd.js";
  karrotPixelScript.async = true;
  document.head.appendChild(karrotPixelScript);

  karrotPixelScript.onload = () => {
    if (window.karrotPixel) {
      try {
        const pixelId = "1735542452748400001";
        window.karrotPixel.init(pixelId);
        window.karrotPixel.track("ViewPage");
        console.log(
          `Karrot Pixel initialized with ID: ${pixelId} and ViewPage event tracked.`
        );
      } catch (error) {
        console.error("Error initializing Karrot Pixel:", error);
      }
    } else {
      console.warn(
        "Karrot Pixel script loaded, but karrotPixel is undefined."
      );
    }
  };

  console.log("Karrot Pixel script loaded.");
};

// ==================== 통합 폼 제출 핸들러 ====================
/**
 * 폼 제출 처리 (크래프트 여부 자동 판단)
 * @param {Object} formData - 폼 데이터
 * @param {string} [apiUrl] - 크래프트 API URL (있으면 크래프트로 처리)
 */
export const submitForm = async (formData, apiUrl = null) => {
  const requestData = {
    ...formData,
    ...(apiUrl && { apiUrl }),
  };

  try {
    let response;
    if (apiUrl) {
      response = await submitCraftCustomerForm(requestData);
    } else {
      response = await submitCustomerForm(requestData);
    }

    // 성공 시 추적 이벤트 발생
    if (response.status >= 200 && response.status < 300) {
      trackGoogleAnalytics({ phoneNumber: formData.phoneNumber });
      trackFacebookPixel();
      trackKarrotPixel("SubmitApplication");
    }

    return response;
  } catch (error) {
    console.error("API 호출 실패, 백업 API 시도:", error);
    
    // 백업 API 호출 시도
    try {
      console.log("백업 datalog API 호출 중...");
      await submitBackupLog({
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        selectedOption: formData.selectedOption,
        date: formData.date,
        urlCode: formData.urlCode,
        urlCodeSettingId: formData.urlCodeSettingId
      });
      console.log("백업 API 성공");
    } catch (backupError) {
      console.error("백업 API도 실패, 이메일 알림 전송:", backupError);
      
      // 백업 API도 실패하면 이메일 알림
      try {
        await sendErrorNotification({
          name: formData.name,
          phoneNumber: formData.phoneNumber,
          date: formData.date,
          urlCode: formData.urlCode,
          errorMessage: `메인 API: ${error.message}, 백업 API: ${backupError.message}`
        });
      } catch (emailError) {
        console.error("이메일 알림도 실패:", emailError);
      }
    }
    
    // 원래 에러를 다시 throw
    throw error;
  }
};